import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  clearPromoActiveTemplate,
  createPromoTemplate,
  deletePromoTemplate,
  getPromoPayload,
  getPromoTemplate,
  isPromoApiSuccess,
  listPromoSurfaces,
  setPromoActiveTemplate,
  setPromoSurfaceEnabled,
  updatePromoTemplate,
} from '../../services/promoSurfacesApi';
import PromoIngestPanel from './PromoIngestPanel';
import {
  activateResultMessage,
  buildTemplateBody,
  canActivateTemplate,
  clearActiveMessage,
  createResultMessage,
  describeBlockers,
  emptyTemplateForm,
  getPromoSurface,
  parseSurfaceList,
  parseTemplate,
  statusLabel,
  surfaceSwitchMessage,
  templateAcceptsIngest,
  templateBlockers,
  templateToForm,
  validateTemplateForm,
  warningLabel,
} from './promoSurfacesUtils';
import { usePromoSurfacesSession } from './usePromoSurfacesSession';

function NumberField({ label, value, onChange, helperText, min = 0 }) {
  return (
    <TextField
      type="number"
      size="small"
      fullWidth
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      helperText={helperText || ' '}
      inputProps={{ min, step: 1 }}
    />
  );
}

function PromoSurfaceEditor() {
  const { surfaceKey, id } = useParams();
  const isNew = !id || id === 'new';
  const surface = getPromoSurface(surfaceKey);
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState(() => emptyTemplateForm(surfaceKey));
  const [baseline, setBaseline] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(null);
  const [working, setWorking] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { handleApiResponse } = usePromoSurfacesSession(setError);

  const applyTemplate = useCallback(
    (next) => {
      const nextForm = templateToForm(next, surfaceKey);
      setTemplate(next);
      setForm(nextForm);
      setBaseline(JSON.stringify(nextForm));
    },
    [surfaceKey]
  );

  const reload = useCallback(async () => {
    if (!surface || isNew) return;
    const result = await getPromoTemplate(surface.key, id);
    if (handleApiResponse(result, 'Could not load this template.')) return;
    applyTemplate(parseTemplate(getPromoPayload(result)));
  }, [applyTemplate, handleApiResponse, id, isNew, surface]);

  const loadSlot = useCallback(async () => {
    if (!surface) return;
    const result = await listPromoSurfaces({ keys: surface.key });
    if (!isPromoApiSuccess(result)) return;
    const match = parseSurfaceList(getPromoPayload(result)).surfaces.find((item) => item.key === surface.key) || null;
    setSlot(match);
  }, [surface]);

  useEffect(() => {
    loadSlot();
  }, [loadSlot]);

  useEffect(() => {
    if (!surface) return;
    if (isNew) {
      const nextForm = emptyTemplateForm(surface.key);
      setForm(nextForm);
      setBaseline(JSON.stringify(nextForm));
      setTemplate(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    getPromoTemplate(surface.key, id)
      .then((result) => {
        if (cancelled) return;
        if (handleApiResponse(result, 'Could not load this template.')) return;
        applyTemplate(parseTemplate(getPromoPayload(result)));
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.message || 'Could not load this template.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [applyTemplate, handleApiResponse, id, isNew, surface]);

  useEffect(() => {
    const notice = location.state?.notice;
    if (!notice) return;
    setSnackbar({ open: true, message: notice, severity: 'success' });
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const updateDisplay = (key, value) => {
    setForm((current) => ({ ...current, display: { ...current.display, [key]: value } }));
  };

  const handleSave = async () => {
    if (!surface) return;
    const problem = validateTemplateForm(form, surface.key);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const body = buildTemplateBody(form, surface.key, { includeStatus: isNew });
      const result = isNew
        ? await createPromoTemplate(surface.key, body)
        : await updatePromoTemplate(surface.key, id, body);
      if (handleApiResponse(result, 'Could not save this template.')) return;
      if (isNew) {
        const payload = getPromoPayload(result);
        const saved = parseTemplate(payload);
        if (!saved?.id) {
          setError('The template was saved, but the response had no id.');
          return;
        }
        navigate(`/admin/promo/${surface.key}/${saved.id}`, {
          replace: true,
          state: { notice: createResultMessage(payload) },
        });
        return;
      }
      await reload();
      setSnackbar({ open: true, message: 'Saved.', severity: 'success' });
    } catch (saveError) {
      setError(saveError.message || 'Could not save this template.');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!pending || !surface || !template) return;
    setWorking(true);
    setError('');
    try {
      if (pending === 'activate') {
        const result = await setPromoActiveTemplate(surface.key, template.id);
        if (handleApiResponse(result, 'Could not make this template active.')) return;
        setSnackbar({ open: true, message: activateResultMessage(getPromoPayload(result)), severity: 'success' });
        setPending(null);
        await Promise.all([reload(), loadSlot()]);
      } else if (pending === 'clear') {
        const result = await clearPromoActiveTemplate(surface.key);
        if (handleApiResponse(result, 'Could not remove this template from the slot.')) return;
        setSnackbar({ open: true, message: clearActiveMessage(surface.title), severity: 'success' });
        setPending(null);
        await Promise.all([reload(), loadSlot()]);
      } else if (pending === 'delete') {
        const result = await deletePromoTemplate(surface.key, template.id);
        if (handleApiResponse(result, 'Could not delete this template.')) return;
        navigate(`/admin/promo/${surface.key}`, { state: { notice: `Deleted ${template.name}.` } });
      }
    } catch (actionError) {
      setError(actionError.message || 'The request failed.');
    } finally {
      setWorking(false);
    }
  };

  const handleSurfaceToggle = async (enabled) => {
    if (!surface) return;
    setWorking(true);
    setError('');
    try {
      const result = await setPromoSurfaceEnabled(surface.key, enabled);
      if (handleApiResponse(result, enabled ? 'Could not turn this surface on.' : 'Could not turn this surface off.')) {
        return;
      }
      setSnackbar({
        open: true,
        message: surfaceSwitchMessage(getPromoPayload(result), surface.title),
        severity: 'success',
      });
      await loadSlot();
    } catch (toggleError) {
      setError(toggleError.message || 'Could not update this surface.');
    } finally {
      setWorking(false);
    }
  };

  if (!surface) {
    return <Alert severity="error">Unknown slot.</Alert>;
  }

  if (loading) return <CircularProgress size={28} />;

  const dirty = JSON.stringify(form) !== baseline;
  const blockers = template ? templateBlockers(template) : [];
  const blockReason = describeBlockers(blockers);
  const canActivate = template ? canActivateTemplate(template) : false;
  const templateIsActive = template?.is_active === true || template?.status === 'active';
  const slotWarning = warningLabel(slot?.warning);
  const showVideoSource = surface.key !== 'corner_ribbon' && form.creative === 'video';
  const showIngest =
    !isNew && form.creative === 'video' && form.video_source === 'transcode' && templateAcceptsIngest(template);
  const ingestNeedsSave = !isNew && form.creative === 'video' && form.video_source === 'transcode' && !showIngest;

  return (
    <Box>
      <Button component={Link} to={`/admin/promo/${surface.key}`} size="small" sx={{ mb: 1 }}>
        {surface.title} library
      </Button>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6">{isNew ? `New ${surface.title.toLowerCase()}` : template?.name || 'Template'}</Typography>
        {!isNew && template?.status && <Chip size="small" label={statusLabel(template.status)} />}
        {!isNew && template?.activate_on_ready && <Chip size="small" label="Activates when ready" />}
      </Box>

      {slot && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(slot.enabled)}
                disabled={working || saving}
                onChange={(event) => handleSurfaceToggle(event.target.checked)}
                inputProps={{ 'aria-label': `${surface.title} surface` }}
              />
            }
            label={slot.enabled ? 'On' : 'Off'}
          />
          <Chip
            size="small"
            color={slot.serving ? 'success' : 'default'}
            label={slot.serving ? 'Showing in the app' : 'Not showing'}
          />
          <Typography variant="body2" color="text.secondary">
            {slot.enabled
              ? 'The app shows this slot when the active template is eligible.'
              : 'This surface is off. The active template stays active.'}
          </Typography>
        </Box>
      )}
      {slotWarning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {slotWarning}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Template name"
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                helperText="Ops label only. The app does not receive this. 1–120 characters."
                inputProps={{ maxLength: 120 }}
              />
            </Grid>
            {isNew && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.make_active !== false}
                      onChange={(event) => setForm((current) => ({ ...current, make_active: event.target.checked }))}
                    />
                  }
                  label="Make active"
                />
                <Typography variant="body2" color="text.secondary">
                  Leave this off to save a draft. When it is on, an eligible ribbon or Lottie becomes the active
                  template and the previous one on this slot becomes a draft. A video that still needs conversion stays
                  a draft and becomes active when it is ready. This does not change the surface On/Off switch.
                </Typography>
              </Grid>
            )}
            {!isNew && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Save updates the name, copy, display rules, and creative. Active or draft stays as it is. The surface
                  switch is what hides the slot in the app.
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Label"
                value={form.label}
                onChange={(event) => setForm((current) => ({ ...current, label: event.target.value }))}
                helperText="Optional. A blank label does not hide the slot. About 32 characters. 80 is the limit."
                inputProps={{ maxLength: 80 }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                label="Link"
                value={form.link}
                onChange={(event) => setForm((current) => ({ ...current, link: event.target.value }))}
                placeholder="/reels, dugout://arcade, or https://…"
                InputLabelProps={{ shrink: true }}
                helperText="Optional here. Leave it blank and this template stays a draft. A /path, a dugout:// link, or an https:// URL."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Minimum app version"
                value={form.min_app_version}
                onChange={(event) => setForm((current) => ({ ...current, min_app_version: event.target.value }))}
                placeholder="2.3.0"
                InputLabelProps={{ shrink: true }}
                helperText="Optional inclusive floor. Empty means no floor. 2.3 and v2.3.0 are saved as 2.3.0."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Maximum app version"
                value={form.max_app_version}
                onChange={(event) => setForm((current) => ({ ...current, max_app_version: event.target.value }))}
                placeholder="3.1.1"
                InputLabelProps={{ shrink: true }}
                helperText="Optional inclusive ceiling. Empty means no ceiling. The app payload does not include these."
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            How often it appears
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <NumberField
                label="Max shows per session"
                value={form.display.max_shows_per_session}
                onChange={(value) => updateDisplay('max_shows_per_session', value)}
                helperText="0 means never show."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <NumberField
                label="Max shows per day"
                value={form.display.max_shows_per_day}
                onChange={(value) => updateDisplay('max_shows_per_day', value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <NumberField
                label="Delay before show (seconds)"
                value={form.display.delay_before_show_sec}
                onChange={(value) => updateDisplay('delay_before_show_sec', value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <NumberField
                label="Visible duration (seconds)"
                min={1}
                value={form.display.visible_duration_sec}
                onChange={(value) => updateDisplay('visible_duration_sec', value)}
                helperText="At least 1 second."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <NumberField
                label="Cooldown (seconds)"
                value={form.display.cooldown_sec}
                onChange={(value) => updateDisplay('cooldown_sec', value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.display.suppress_after_tap_for_session}
                    onChange={(event) => updateDisplay('suppress_after_tap_for_session', event.target.checked)}
                  />
                }
                label="Hide for the rest of the session after a tap"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {surface.key === 'tab_bar' && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1">Creative</Typography>
            <RadioGroup
              value={form.creative}
              onChange={(event) => setForm((current) => ({ ...current, creative: event.target.value }))}
            >
              <FormControlLabel value="lottie_remote" control={<Radio />} label="Remote Lottie" />
              <FormControlLabel value="video" control={<Radio />} label="Video" />
            </RadioGroup>
          </CardContent>
        </Card>
      )}

      {showVideoSource && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1">Video</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Paste an HLS playlist, or upload an MP4 and wait for conversion. A playlist and an MP4 upload cannot be
              saved together. Saving a direct playlist clears an in-progress MP4 upload. Saving MP4 upload again keeps
              the current video until you start a new upload.
            </Typography>
            <RadioGroup
              value={form.video_source}
              onChange={(event) => setForm((current) => ({ ...current, video_source: event.target.value }))}
            >
              <FormControlLabel value="transcode" control={<Radio />} label="Upload an MP4" />
              <FormControlLabel value="direct_m3u8" control={<Radio />} label="Paste an HLS URL" />
            </RadioGroup>
            {form.video_source === 'direct_m3u8' && (
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="HLS playlist URL"
                    value={form.m3u8_url}
                    onChange={(event) => setForm((current) => ({ ...current, m3u8_url: event.target.value }))}
                    placeholder="https://cdn.example.com/promo/corner.m3u8"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="MP4 URL"
                    value={form.mp4_url}
                    onChange={(event) => setForm((current) => ({ ...current, mp4_url: event.target.value }))}
                    helperText="Optional."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Poster URL"
                    value={form.thumbnail_url}
                    onChange={(event) => setForm((current) => ({ ...current, thumbnail_url: event.target.value }))}
                    helperText="Optional. The MP4 converter does not create one."
                  />
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {form.creative === 'lottie_remote' && surface.key === 'tab_bar' && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <TextField
              fullWidth
              size="small"
              label="Lottie URL"
              value={form.lottie_uri}
              onChange={(event) => setForm((current) => ({ ...current, lottie_uri: event.target.value }))}
              placeholder="https://cdn.example.com/promo/tab.json"
              InputLabelProps={{ shrink: true }}
              helperText="Remote Lottie JSON. This slot cannot take an MP4 upload."
            />
          </CardContent>
        </Card>
      )}

      {showIngest && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <PromoIngestPanel
              surfaceKey={surface.key}
              template={template}
              disabled={dirty}
              onReload={reload}
              onNotice={(message) => setSnackbar({ open: true, message, severity: 'success' })}
            />
          </CardContent>
        </Card>
      )}
      {ingestNeedsSave && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Save this template as an MP4 upload before choosing a file. A Lottie template cannot take an upload, and a
          direct playlist has to be saved as an upload first.
        </Alert>
      )}

      {!isNew && blockReason && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {blockReason}
        </Alert>
      )}
      {!isNew && template?.activate_on_ready && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This template becomes active when the video is ready, if it is still eligible.
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isNew && templateIsActive && (
            <Button color="warning" disabled={saving || working} onClick={() => setPending('clear')}>
              Remove from slot
            </Button>
          )}
          {!isNew && (
            <Button color="error" disabled={saving || working} onClick={() => setPending('delete')}>
              Delete template
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isNew && !templateIsActive && (
            <Button
              variant="outlined"
              disabled={!canActivate || dirty || saving || working}
              onClick={() => setPending('activate')}
            >
              Make active
            </Button>
          )}
          <Button variant="contained" onClick={handleSave} disabled={saving || working}>
            {saving ? 'Saving…' : isNew ? 'Create template' : 'Save changes'}
          </Button>
        </Box>
      </Box>
      {!isNew && dirty && !templateIsActive && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'right' }}>
          Save changes before making this active.
        </Typography>
      )}

      <Dialog open={Boolean(pending)} onClose={() => !working && setPending(null)}>
        <DialogTitle>
          {pending === 'delete'
            ? 'Delete this template?'
            : pending === 'clear'
              ? 'Remove this template from the slot?'
              : 'Make this template active?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pending === 'delete' &&
              `Delete “${template?.name}”? This cannot be undone. If it is the active template, this slot then has no active template. The surface switch stays as it is.`}
            {pending === 'clear' &&
              `“${template?.name}” becomes a draft. The On/Off switch stays as it is.`}
            {pending === 'activate' &&
              `“${template?.name}” becomes the active ${surface.title.toLowerCase()}. The previous active template on this slot becomes a draft. The On/Off switch stays as it is, and the other slots do not change.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={working}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={pending === 'delete' ? 'error' : 'primary'}
            onClick={handleConfirm}
            disabled={working}
          >
            {working ? 'Working…' : pending === 'delete' ? 'Delete' : pending === 'clear' ? 'Remove' : 'Make it active'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((current) => ({ ...current, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PromoSurfaceEditor;
